import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Checkbox,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  Badge,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BuildIcon from '@mui/icons-material/Build';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ContactsIcon from '@mui/icons-material/Contacts';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SyncIcon from '@mui/icons-material/Sync';
import ShieldIcon from '@mui/icons-material/Shield';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PaymentsIcon from '@mui/icons-material/Payments';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import QueueIcon from '@mui/icons-material/Queue';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND } from '../../config/brand';
import BrowserChromeTint from '../common/BrowserChromeTint';
import { SuperAdminRemindersProvider } from '../../contexts/SuperAdminRemindersContext';
import SuperAdminRemindersBell from './SuperAdminRemindersBell';
import SuperAdminRemindersNotifier from './SuperAdminRemindersNotifier';
import { getJobSummary } from '../../services/JobQueueService';
import { getPosErrorBadge } from '../../services/PosErrorAdminService';

// ============================================================================
// SUPERADMIN ORTAK LAYOUT (responsive)
// ============================================================================
// Daha önce her superadmin sayfası kendi sidebar/header kopyasını taşıyordu
// (menü listeleri birbirinden farklılaşmıştı) ve hiçbiri mobil uyumlu değildi.
// Bu bileşen tek kaynak:
//  - Masaüstü (md+): sabit, daraltılabilir sidebar (240px / 70px) + AppBar.
//  - Mobil (<md): sidebar YOK; AppBar'daki hamburger geçici Drawer menü açar.
//  - İçerik: AppBar altında, responsive padding'li, yatay taşmaya kapalı alan.
// Kullanım:
//   <SuperAdminLayout title="Personeller" icon={<PeopleAltIcon />}>...</SuperAdminLayout>
// ============================================================================

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 70;

/** Sol menü başlık görünürlüğü — kullanıcı tercihi, tarayıcıda kalıcı. Eksik anahtar = görünür. */
const MENU_VISIBILITY_KEY = 'gts_superadmin_visible_menu';
const DEVELOPER_GROUP_ID = 'developerTools';

/**
 * Tüm superadmin sayfalarında ortak menü (tek kaynak).
 * roles: öğeyi görebilecek roller — bayi (dealer) yalnızca Şirketler'i görür.
 * Menü filtresi UI içindir; rota koruması App.tsx RequireRole + backend guard'larındadır.
 */
type MenuItem = { id: string; label: string; path: string; icon: React.ReactElement; roles: string[] };

const MENU_ITEMS: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/superadmin/dashboard', icon: <DashboardIcon />, roles: ['superadmin'] },
  { id: 'companies', label: 'Şirketler', path: '/superadmin', icon: <BusinessIcon />, roles: ['superadmin', 'dealer'] },
  { id: 'dealers', label: 'Bayiler', path: '/superadmin/dealers', icon: <StorefrontIcon />, roles: ['superadmin'] },
  { id: 'personnel', label: 'Personeller', path: '/superadmin/personnel', icon: <PeopleAltIcon />, roles: ['superadmin'] },
  { id: 'contacts', label: 'İletişim Listesi', path: '/superadmin/contacts', icon: <ContactsIcon />, roles: ['superadmin'] },
  // Katalog ana DB'de duran global referans listesidir; bayiler yönetemez.
  { id: 'catalog', label: 'Katalog Ürünleri', path: '/superadmin/catalog', icon: <Inventory2Icon />, roles: ['superadmin'] },
  { id: 'billing', label: 'Tahsilat', path: '/superadmin/billing', icon: <PaymentsIcon />, roles: ['superadmin'] },
  { id: 'deletionRequests', label: 'Silme Talepleri', path: '/superadmin/deletion-requests', icon: <DeleteSweepIcon />, roles: ['superadmin'] },
  // Ortak iş kuyruğu: ölü işler ve elle müdahale. Yanındaki sayı = ölü iş sayısı.
  { id: 'jobQueue', label: 'İş Kuyruğu', path: '/superadmin/job-queue', icon: <QueueIcon />, roles: ['superadmin'] },
  // Kasada "Ödeme Kaydı Hatası" olayları. Yanındaki sayı = kayıp şüphesi (para alındı, satış yok).
  { id: 'posErrors', label: 'Kasa Hataları', path: '/superadmin/pos-errors', icon: <PointOfSaleIcon />, roles: ['superadmin'] },
];

// Sistem araçları — tek bir açılır ana sekme altında toplanır (yalnız superadmin).
const DEVELOPER_MENU_ITEMS: MenuItem[] = [
  { id: 'userMigration', label: 'User Migration', path: '/superadmin/user-migration', icon: <SyncIcon />, roles: ['superadmin'] },
  { id: 'permissionMigration', label: 'Yetki Taşıma', path: '/superadmin/permission-migration', icon: <ShieldIcon />, roles: ['superadmin'] },
  { id: 'middlewareMonitor', label: 'API Güvenlik', path: '/superadmin/middleware-monitor', icon: <ShieldIcon />, roles: ['superadmin'] },
  { id: 'systemMonitor', label: 'Sistem İzleme', path: '/superadmin/system-monitor', icon: <MonitorHeartIcon />, roles: ['superadmin'] },
  { id: 'mailReports', label: 'Mail Raporlari', path: '/superadmin/mail-reports', icon: <MarkEmailReadIcon />, roles: ['superadmin'] },
];

const loadMenuVisibility = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(MENU_VISIBILITY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

/** Kayıtta false yoksa başlık görünür. */
const isMenuTitleVisible = (visibility: Record<string, boolean>, id: string) => visibility[id] !== false;

const SidebarMenuItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'isCollapsed',
})<{ active?: boolean; isCollapsed?: boolean }>(({ theme, active, isCollapsed }) => ({
  padding: theme.spacing(1.25, isCollapsed ? 2 : 3),
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  borderLeft: active ? '4px solid white' : '4px solid transparent',
  backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
}));

interface SuperAdminLayoutProps {
  /** AppBar'da görünen sayfa başlığı. */
  title: string;
  /** Başlığın solunda görünen ikon. */
  icon?: React.ReactNode;
  /** İçerik Container genişliği; false → Container kullanılmaz (tam genişlik). */
  maxWidth?: 'lg' | 'xl' | false;
  /** İçeriğin kendi scroll'unu yönettiği durumlar için padding'i kaldırır. */
  disablePadding?: boolean;
  children: React.ReactNode;
}

/** Sidebar/Drawer'ın ortak iç içeriği (logo + menü + footer). */
const SidebarContent: React.FC<{
  isCollapsed: boolean;
  onNavigate: (path: string) => void;
  menuVisibility: Record<string, boolean>;
  onOpenMenuSettings: () => void;
}> = ({ isCollapsed, onNavigate, menuVisibility, onOpenMenuSettings }) => {
  const location = useLocation();
  const { userDetails, isDealer } = useAuth();

  const role = (userDetails?.role || '').toLowerCase();
  const shown = (id: string) => isMenuTitleVisible(menuVisibility, id);
  const visibleMenuItems = MENU_ITEMS.filter((item) => item.roles.includes(role) && shown(item.id));
  const visibleDeveloperItems = DEVELOPER_MENU_ITEMS.filter(
    (item) => item.roles.includes(role) && shown(item.id)
  );
  const showDeveloperGroup = shown(DEVELOPER_GROUP_ID) && visibleDeveloperItems.length > 0;

  // İş Kuyruğu menüsündeki ölü iş sayısı (yalnız süper admin; dakikada bir).
  const [deadJobs, setDeadJobs] = useState(0);
  useEffect(() => {
    if (role !== 'superadmin') return undefined;
    let alive = true;
    const tick = () =>
      getJobSummary()
        .then((s) => alive && setDeadJobs(s.total?.dead ?? 0))
        .catch(() => undefined);
    tick();
    const t = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [role]);

  // Kasa Hataları menüsündeki kayıp şüphesi sayısı (yalnız süper admin; dakikada bir).
  const [lostPosErrors, setLostPosErrors] = useState(0);
  useEffect(() => {
    if (role !== 'superadmin') return undefined;
    let alive = true;
    const tick = () =>
      getPosErrorBadge()
        .then((b) => alive && setLostPosErrors(b.lostSuspicion ?? 0))
        .catch(() => undefined);
    tick();
    const t = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [role]);

  const badgeCount = (id: string) => (id === 'jobQueue' ? deadJobs : id === 'posErrors' ? lostPosErrors : 0);
  const badgeLabel = (id: string, n: number) => (id === 'posErrors' ? `${n} kayıp şüphesi` : `${n} ölü iş`);

  // Aktif rota developer araçlarından biriyse grup otomatik açık başlar.
  const isDeveloperRouteActive = visibleDeveloperItems.some((item) => item.path === location.pathname);
  const [devOpen, setDevOpen] = useState<boolean>(isDeveloperRouteActive);

  // Rota değişip developer aracına girildiğinde grubu açık tut.
  useEffect(() => {
    if (isDeveloperRouteActive) setDevOpen(true);
  }, [isDeveloperRouteActive]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1976d2',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={BRAND.logos.main}
            alt={`${BRAND.name} Logo`}
            sx={{
              width: isCollapsed ? 40 : 70,
              height: isCollapsed ? 40 : 70,
              borderRadius: '10px',
              mb: isCollapsed ? 0 : 1,
              transition: 'all 0.3s ease',
            }}
          />
          {!isCollapsed && (
            <Tooltip title="Sol menü başlıklarını göster / gizle" placement="right">
              <IconButton
                onClick={onOpenMenuSettings}
                sx={{
                  position: 'absolute',
                  left: 5,
                  top: 5,
                  color: 'white',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                }}
              >
                <SettingsIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {!isCollapsed && (
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
            {isDealer ? 'Bayi Paneli' : 'Süper Admin'}
          </Typography>
        )}
        {!isCollapsed && isDealer && userDetails?.firstName && (
          <Typography variant="caption" sx={{ color: 'white', opacity: 0.85 }} noWrap>
            {`${userDetails.firstName} ${userDetails.lastName || ''}`.trim()}
          </Typography>
        )}
      </Box>

      <List
        sx={{
          flexGrow: 1,
          py: 2,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '2px',
          },
        }}
      >
        {visibleMenuItems.map((item) => (
          <SidebarMenuItem
            key={item.path}
            active={location.pathname === item.path}
            isCollapsed={isCollapsed}
            onClick={() => onNavigate(item.path)}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: isCollapsed ? 0 : 40 }}>
              {badgeCount(item.id) > 0 && isCollapsed ? (
                <Badge badgeContent={badgeCount(item.id)} color="error" max={99}>
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText primary={item.label} primaryTypographyProps={{ noWrap: true }} />
            )}
            {!isCollapsed && badgeCount(item.id) > 0 && (
              <Box
                component="span"
                aria-label={badgeLabel(item.id, badgeCount(item.id))}
                sx={{
                  bgcolor: 'white',
                  color: '#c62828',
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: '10px',
                  px: 1,
                  lineHeight: '20px',
                }}
              >
                {badgeCount(item.id) > 99 ? '99+' : badgeCount(item.id)}
              </Box>
            )}
          </SidebarMenuItem>
        ))}

        {/* Developer Araçları — tek açılır ana sekme (yalnız superadmin) */}
        {showDeveloperGroup && (
          <>
            <SidebarMenuItem
              active={isDeveloperRouteActive}
              isCollapsed={isCollapsed}
              onClick={() => setDevOpen((prev) => !prev)}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: isCollapsed ? 0 : 40 }}>
                <BuildIcon />
              </ListItemIcon>
              {!isCollapsed && (
                <>
                  <ListItemText primary="Sistem Araçları" primaryTypographyProps={{ noWrap: true }} />
                  {devOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </>
              )}
            </SidebarMenuItem>
            <Collapse in={devOpen} timeout="auto" unmountOnExit>
              {visibleDeveloperItems.map((item) => (
                <SidebarMenuItem
                  key={item.path}
                  active={location.pathname === item.path}
                  isCollapsed={isCollapsed}
                  onClick={() => onNavigate(item.path)}
                  sx={{ pl: isCollapsed ? undefined : 5 }}
                >
                  <ListItemIcon sx={{ color: 'white', minWidth: isCollapsed ? 0 : 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ noWrap: true, fontSize: '0.9rem' }}
                    />
                  )}
                </SidebarMenuItem>
              ))}
            </Collapse>
          </>
        )}
      </List>

      <Box
        sx={{
          p: isCollapsed ? 1 : 2,
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8, whiteSpace: 'nowrap' }}>
          {isCollapsed ? '©' : `© ${BRAND.name} ${new Date().getFullYear()}`}
        </Typography>
      </Box>
    </Box>
  );
};

const SuperAdminLayoutInner: React.FC<SuperAdminLayoutProps> = ({
  title,
  icon,
  maxWidth = 'xl',
  disablePadding = false,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrowDesktop = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const navigate = useNavigate();
  const { currentUser, logout, userDetails } = useAuth();
  const role = (userDetails?.role || '').toLowerCase();

  // Masaüstü: dar ekranlarda (md-lg) varsayılan daraltılmış başlar
  const [collapsed, setCollapsed] = useState<boolean>(() => isNarrowDesktop);
  // Mobil: geçici drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Sol menü başlık görünürlüğü — dashboard widget'ları gibi localStorage'da kalıcı.
  const [menuVisibility, setMenuVisibility] = useState<Record<string, boolean>>(loadMenuVisibility);
  const [menuSettingsOpen, setMenuSettingsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(MENU_VISIBILITY_KEY, JSON.stringify(menuVisibility));
    } catch {
      /* kota/erişim hatası sessiz geçilir */
    }
  }, [menuVisibility]);

  const roleMenuItems = useMemo(
    () => MENU_ITEMS.filter((item) => item.roles.includes(role)),
    [role]
  );
  const roleDeveloperItems = useMemo(
    () => DEVELOPER_MENU_ITEMS.filter((item) => item.roles.includes(role)),
    [role]
  );

  const hiddenMenuCount = useMemo(() => {
    const ids = [
      ...roleMenuItems.map((item) => item.id),
      ...(roleDeveloperItems.length > 0 ? [DEVELOPER_GROUP_ID] : []),
      ...roleDeveloperItems.map((item) => item.id),
    ];
    return ids.filter((id) => !isMenuTitleVisible(menuVisibility, id)).length;
  }, [menuVisibility, roleMenuItems, roleDeveloperItems]);

  const toggleMenuTitle = (id: string) => {
    setMenuVisibility((prev) => ({ ...prev, [id]: !isMenuTitleVisible(prev, id) }));
  };

  const showAllMenuTitles = () => setMenuVisibility({});

  // Filtre/Select/Autocomplete açılınca MUI body scroll-lock uygulayıp scrollbar'ı
  // gizler; sayfa sağa-sola kayar. Superadmin'de kilit kapalı, scrollbar yeri sabit.
  const noShiftTheme = useMemo(
    () =>
      createTheme(theme, {
        components: {
          MuiModal: { defaultProps: { disableScrollLock: true } },
          MuiPopover: { defaultProps: { disableScrollLock: true } },
          MuiMenu: { defaultProps: { disableScrollLock: true } },
        },
      }),
    [theme]
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlGutter = html.style.scrollbarGutter;
    const prevBodyGutter = body.style.scrollbarGutter;
    html.style.scrollbarGutter = 'stable';
    body.style.scrollbarGutter = 'stable';
    return () => {
      html.style.scrollbarGutter = prevHtmlGutter;
      body.style.scrollbarGutter = prevBodyGutter;
    };
  }, []);

  const sidebarWidth = isMobile ? 0 : collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const handleNavigate = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setAnchorEl(null);
      navigate('/login');
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };

  return (
    <ThemeProvider theme={noShiftTheme}>
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default', overflowX: 'hidden' }}>
      {/* Tarayıcı arayüzü süperadmin mavisini alsın */}
      <BrowserChromeTint color="#1976d2" />

      {/* Masaüstü: sabit sidebar */}
      {!isMobile && (
        <Box
          sx={{
            width: sidebarWidth,
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1200,
            boxShadow: '0 0 10px rgba(0,0,0,0.3)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <SidebarContent
            isCollapsed={collapsed}
            onNavigate={handleNavigate}
            menuVisibility={menuVisibility}
            onOpenMenuSettings={() => setMenuSettingsOpen(true)}
          />
          <IconButton
            onClick={() => setCollapsed((p) => !p)}
            size="small"
            sx={{
              position: 'absolute',
              right: -12,
              top: 55,
              bgcolor: '#1976d2',
              color: 'white',
              border: '2px solid white',
              boxShadow: '0 0 5px rgba(0,0,0,0.2)',
              zIndex: 1201,
              '&:hover': { bgcolor: '#1976d2', opacity: 0.9 },
            }}
          >
            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {/* Mobil: geçici drawer menü */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: EXPANDED_WIDTH, border: 'none' } }}
        >
          <SidebarContent
            isCollapsed={false}
            onNavigate={handleNavigate}
            menuVisibility={menuVisibility}
            onOpenMenuSettings={() => {
              setDrawerOpen(false);
              setMenuSettingsOpen(true);
            }}
          />
        </Drawer>
      )}

      {/* Sağ taraf: AppBar + içerik */}
      <Box sx={{ flex: 1, minWidth: 0, ml: `${sidebarWidth}px` }}>
        <AppBar
          elevation={0}
          sx={{
            position: 'fixed',
            bgcolor: '#1976d2',
            color: 'white',
            boxShadow: '0px 1px 4px rgba(0,0,0,0.1)',
            width: `calc(100% - ${sidebarWidth}px)`,
            ml: `${sidebarWidth}px`,
            zIndex: 1100,
            transition: theme.transitions.create(['width', 'margin-left'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 64 } }}>
            {isMobile && (
              <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {icon}
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                component="h1"
                noWrap
                sx={{ fontWeight: 'bold' }}
              >
                {title}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {/* Yenileme hatırlatmaları — zil ve sağ alt bant aynı kaynaktan beslenir */}
            <SuperAdminRemindersBell />
            <Avatar
              sx={{
                bgcolor: 'primary.dark',
                cursor: 'pointer',
                width: { xs: 34, md: 40 },
                height: { xs: 34, md: 40 },
              }}
              onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
            >
              {currentUser?.email?.charAt(0).toUpperCase()}
            </Avatar>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { width: 200, mt: 1 } }}
            >
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Çıkış Yap
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* İçerik */}
        <Box
          sx={{
            mt: { xs: 7, md: 8 },
            p: disablePadding ? 0 : { xs: 1.5, sm: 2, md: 3 },
            minWidth: 0,
            overflowX: 'hidden',
          }}
        >
          {maxWidth === false ? (
            children
          ) : (
            <Container maxWidth={maxWidth} disableGutters sx={{ minWidth: 0 }}>
              {children}
            </Container>
          )}
        </Box>
      </Box>

      {/* Sağ altta açılan yenileme uyarı bandı (günlük, kapatılabilir) */}
      <SuperAdminRemindersNotifier />

      <Dialog
        open={menuSettingsOpen}
        onClose={() => setMenuSettingsOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Sol Menü Başlıkları</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Görünür başlıklar sol menüde kalır. Gizlenen sayfaya adresle hâlâ gidilebilir.
          </Typography>
          <List dense disablePadding>
            {roleMenuItems.map((item) => (
              <ListItem
                key={item.id}
                disablePadding
                onClick={() => toggleMenuTitle(item.id)}
                sx={{ cursor: 'pointer', borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={isMenuTitleVisible(menuVisibility, item.id)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
            {roleDeveloperItems.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem
                  disablePadding
                  onClick={() => toggleMenuTitle(DEVELOPER_GROUP_ID)}
                  sx={{ cursor: 'pointer', borderRadius: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      edge="start"
                      checked={isMenuTitleVisible(menuVisibility, DEVELOPER_GROUP_ID)}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText primary="Sistem Araçları" secondary="Grup başlığı" />
                </ListItem>
                {roleDeveloperItems.map((item) => (
                  <ListItem
                    key={item.id}
                    disablePadding
                    onClick={() => {
                      if (!isMenuTitleVisible(menuVisibility, DEVELOPER_GROUP_ID)) return;
                      toggleMenuTitle(item.id);
                    }}
                    sx={{
                      cursor: isMenuTitleVisible(menuVisibility, DEVELOPER_GROUP_ID) ? 'pointer' : 'default',
                      borderRadius: 1,
                      pl: 2,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={isMenuTitleVisible(menuVisibility, item.id)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                        disabled={!isMenuTitleVisible(menuVisibility, DEVELOPER_GROUP_ID)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        color: isMenuTitleVisible(menuVisibility, DEVELOPER_GROUP_ID)
                          ? 'text.primary'
                          : 'text.disabled',
                      }}
                    />
                  </ListItem>
                ))}
              </>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={showAllMenuTitles}
            disabled={hiddenMenuCount === 0}
            startIcon={<RestartAltIcon />}
          >
            Tümünü Göster
          </Button>
          <Button variant="contained" onClick={() => setMenuSettingsOpen(false)}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
};

/**
 * Hatırlatma sağlayıcısı layout'un dışında: zil (AppBar) ve uyarı bandı
 * (sağ alt) aynı veriyi tek istekten paylaşsın diye. Provider her süperadmin
 * sayfasını sardığından hatırlatmalar panele her girişte çalışır.
 */
const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = (props) => (
  <SuperAdminRemindersProvider>
    <SuperAdminLayoutInner {...props} />
  </SuperAdminRemindersProvider>
);

export default SuperAdminLayout;
